import { useEffect, useState } from 'react'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { SignIn, useAuth, useClerk } from '@clerk/clerk-react'
import loginBgImg from '@/assets/images/login-bg.png'
import logoImg from '@/assets/images/greybull-logo-white.svg'

type LoginSearch = {
  next?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = createFileRoute('/' as any)({
  validateSearch: (search: Record<string, unknown>): LoginSearch => {
    const next = search.next
    return typeof next === 'string' && next.length > 0 ? { next } : {}
  },
  component: LoginPage,
})

function hasLogoutRequest(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.get('logout') === '1'
}

function SignInSkeleton() {
  return (
    <div className="w-full max-w-sm mx-auto animate-pulse">
      <div className="border border-border rounded-lg p-6 space-y-6">
        <div className="flex justify-center">
          <div className="h-10 w-24 bg-muted rounded" />
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="h-4 w-20 bg-muted rounded" />
            <div className="h-10 w-full bg-muted rounded" />
          </div>
          <div className="h-10 w-full bg-muted rounded" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-px flex-1 bg-muted" />
          <div className="h-4 w-8 bg-muted rounded" />
          <div className="h-px flex-1 bg-muted" />
        </div>
        <div className="space-y-2">
          <div className="h-10 w-full bg-muted rounded" />
          <div className="h-10 w-full bg-muted rounded" />
        </div>
      </div>
    </div>
  )
}

function LoginPage() {
  const { isSignedIn, isLoaded } = useAuth()
  const clerk = useClerk()
  const { next: rawNext } = Route.useSearch()
  const [isLogoutRequest] = useState<boolean>(() => hasLogoutRequest())

  // Anonymous OGSM deep links arrive at /auth/redirect?next=<OGSM URL>, which
  // bounces unauthenticated users here. Preserve the `next` across the Clerk
  // sign-in round-trip so /auth/redirect can resume the original handoff.
  const postSignInRedirect = rawNext
    ? `/auth/redirect?next=${encodeURIComponent(rawNext)}`
    : '/auth/redirect'

  useEffect(() => {
    if (!isLogoutRequest || !isLoaded) {
      return
    }

    if (!isSignedIn) {
      window.location.replace('/')
      return
    }

    void clerk.signOut({ redirectUrl: '/' })
  }, [isLogoutRequest, isLoaded, isSignedIn, clerk])

  if (isLogoutRequest) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <SignInSkeleton />
      </div>
    )
  }

  if (isSignedIn) {
    return <Navigate to="/auth/redirect" />
  }

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="relative hidden bg-primary lg:block">
        <img
          src={loginBgImg}
          alt="Login Background"
          className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
        />
        <div className="relative flex items-center justify-center h-full z-10">
          <img
            src={logoImg}
            alt="Logo"
            className="h-[186px] w-auto animate-fade-in"
          />
        </div>
      </div>
      <div className="flex flex-col gap-4 p-4 sm:p-6 md:p-10">
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-sm">
            {!isLoaded ? (
              <SignInSkeleton />
            ) : (
              <SignIn
                appearance={{
                  elements: {
                    logoImage: '!h-10',
                    rootBox: 'mx-auto',
                    cardBox: '!shadow-none !border border-border',
                    formButtonPrimary:
                      '!bg-primary !border-primary !shadow-none',
                    buttonArrowIcon: 'hidden',
                  },
                }}
                routing="hash"
                forceRedirectUrl={postSignInRedirect}
                fallbackRedirectUrl={postSignInRedirect}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
